// Phase 13 · Step 16: "Drop a contract, quote or invoice". The prompt and a tolerant parser for the AI's answer.
// Shared by the `ai` function (hosted) and the browser (browser-only mode). Plain JS, no imports.
// Nothing here saves anything: the review dialog is the only writer, and only when she presses Save.

export const EXTRACT_MAX_BYTES = 10 * 1024 * 1024;
export const EXTRACT_TYPES = { "application/pdf": "document", "image/jpeg": "image", "image/png": "image" };
export const EXTRACT_DOC_TYPES = ["Contract", "Proposal", "Invoice", "Other"];
export const EXTRACT_FIELDS = ["docType", "vendorName", "category", "contactName", "phone", "email", "website", "address",
  "total", "deposit", "depositDue", "balance", "balanceDue", "paymentMethod", "cancellationTerms", "keyTerms", "services"];

export const EXTRACT_SYSTEM = [
  "You read a wedding vendor's contract, quote or invoice and return JSON only, with no other text.",
  "Return exactly one JSON object with these keys:",
  "docType (one of Contract, Proposal, Invoice, Other), vendorName, category, contactName, phone, email, website, address,",
  "total, deposit, depositDue, balance, balanceDue (amounts as plain numbers in US dollars; dates as YYYY-MM-DD),",
  "paymentMethod, cancellationTerms, keyTerms (one or two short sentences each), services (an array of short strings),",
  "confidence (an object giving \"high\" or \"low\" for each key you filled), warnings (an array of short notes about anything unclear).",
  "Use null when a value isn't in the document. Never guess. If the document is hard to read, say so in warnings."
].join(" ");

/** The user turn that goes with the document: the categories she uses, so the AI picks one of hers. */
export function extractPrompt(categories) {
  const cats = (Array.isArray(categories) ? categories : []).map(c => String(c).slice(0, 60)).filter(Boolean).slice(0, 60);
  return "Read this vendor document and fill in the JSON." + (cats.length ? " For category, use one of: " + cats.join(", ") + " (or null if none fits)." : "");
}

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const pad = n => String(n).padStart(2, "0");
const validDay = (y, m, d) => { const t = new Date(Date.UTC(y, m - 1, d)); return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d; };
/** "2026-03-14", "3/14/2026", "March 14, 2026", "14 Mar 2026" → "2026-03-14"; anything else → "". */
export function toYmd(v) {
  const s = String(v == null ? "" : v).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return validDay(+m[1], +m[2], +m[3]) ? m[1] + "-" + pad(m[2]) + "-" + pad(m[3]) : "";
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return validDay(+m[3], +m[1], +m[2]) ? m[3] + "-" + pad(m[1]) + "-" + pad(m[2]) : "";
  const monthNum = w => MONTHS[w.slice(0, 4).toLowerCase()] || MONTHS[w.slice(0, 3).toLowerCase()] || 0;
  const ymd = (y, mo, d) => mo && validDay(+y, mo, +d) ? y + "-" + pad(mo) + "-" + pad(d) : "";
  m = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(s);
  if (m) return ymd(m[3], monthNum(m[1]), m[2]);
  m = /^(\d{1,2})\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})$/.exec(s);
  if (m) return ymd(m[3], monthNum(m[2]), m[1]);
  return "";
}

/** "$4,800.00" → 4800; "4800" → 4800; negative, empty or unreadable → null. */
export function toMoney(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[$,\s]|USD/gi, ""));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}

const text = (v, max) => v == null ? "" : String(v).replace(/\s+/g, " ").trim().slice(0, max);

/** The first {...} block in the AI's answer, parsed. Tolerates prose or ```json fences around it. */
function firstJson(raw) {
  const s = String(raw || "");
  const start = s.indexOf("{");
  if (start < 0) return null;
  // Walk to the matching brace, ignoring braces inside strings.
  let depth = 0, inStr = false, esc = false;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (inStr) { if (esc) esc = false; else if (c === "\\") esc = true; else if (c === "\"") inStr = false; continue; }
    if (c === "\"") inStr = true;
    else if (c === "{") depth++;
    else if (c === "}" && --depth === 0) { try { return JSON.parse(s.slice(start, i + 1)); } catch { return null; } }
  }
  return null;
}

/**
 * The AI's answer → the review form's values. Every field is present (empty when unknown); `confidence[field]` is
 * "high" only when the AI said so and the value survived cleaning, otherwise "low" (the form marks those "Check this").
 * A category is kept only when it is one of hers; dates must be real days; money must be a number ≥ 0.
 */
export function parseExtraction(raw, categories) {
  const j = firstJson(raw);
  const warnings = [];
  if (!j || typeof j !== "object") {
    return { ok: false, fields: emptyFields(), confidence: Object.fromEntries(EXTRACT_FIELDS.map(f => [f, "low"])), warnings: ["I couldn't read that document. Try a clearer scan or a PDF."] };
  }
  const cats = (Array.isArray(categories) ? categories : []).map(String);
  const catMatch = c => { const k = text(c, 60).toLowerCase(); return k ? cats.find(x => x.toLowerCase() === k) || "" : ""; };
  const docType = EXTRACT_DOC_TYPES.find(t => t.toLowerCase() === text(j.docType, 20).toLowerCase()) || "";
  const fields = {
    docType, vendorName: text(j.vendorName, 120), category: catMatch(j.category),
    contactName: text(j.contactName, 120), phone: text(j.phone, 40), email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text(j.email, 160)) ? text(j.email, 160) : "",
    website: text(j.website, 300), address: text(j.address, 300),
    total: toMoney(j.total), deposit: toMoney(j.deposit), depositDue: toYmd(j.depositDue), balance: toMoney(j.balance), balanceDue: toYmd(j.balanceDue),
    paymentMethod: text(j.paymentMethod, 120), cancellationTerms: text(j.cancellationTerms, 600), keyTerms: text(j.keyTerms, 1000),
    services: (Array.isArray(j.services) ? j.services : typeof j.services === "string" ? j.services.split(/[,;\n]/) : []).map(x => text(x, 80)).filter(Boolean).slice(0, 20)
  };
  if (j.category && !fields.category) warnings.push("The document's category (" + text(j.category, 40) + ") isn't one of yours, so pick one.");
  if (j.total != null && j.total !== "" && fields.total == null) warnings.push("The total couldn't be read as an amount.");
  ["depositDue", "balanceDue"].forEach(k => { if (j[k] && !fields[k]) warnings.push("A due date couldn't be read: " + text(j[k], 40) + "."); });
  const said = j.confidence && typeof j.confidence === "object" ? j.confidence : {};
  const filled = k => { const v = fields[k]; return Array.isArray(v) ? v.length > 0 : v != null && v !== ""; };
  const confidence = Object.fromEntries(EXTRACT_FIELDS.map(k => [k, filled(k) && String(said[k] || "").toLowerCase() === "high" ? "high" : "low"]));
  (Array.isArray(j.warnings) ? j.warnings : []).slice(0, 8).forEach(w => { const t = text(w, 200); if (t) warnings.push(t); });
  if (!fields.vendorName) warnings.push("No vendor name found. Type it in before saving.");
  return { ok: true, fields, confidence, warnings: warnings.slice(0, 10) };
}

export function emptyFields() {
  return { docType: "", vendorName: "", category: "", contactName: "", phone: "", email: "", website: "", address: "", total: null, deposit: null, depositDue: "",
    balance: null, balanceDue: "", paymentMethod: "", cancellationTerms: "", keyTerms: "", services: [] };
}

/** Checks a file before it is uploaded or read: PDF, JPG or PNG, at most 10 MB. Returns an error message, or "". */
export function checkExtractFile({ name, type, size }) {
  const ext = ((/\.([a-z0-9]+)$/i.exec(String(name || "")) || [])[1] || "").toLowerCase();
  const t = String(type || "").toLowerCase() || ({ pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" })[ext] || "";
  if (!EXTRACT_TYPES[t]) return "I can read PDFs and photos (JPG or PNG).";
  if (!(Number(size) > 0)) return "That file looks empty. Try choosing it again.";
  if (Number(size) > EXTRACT_MAX_BYTES) return "That file is over 10 MB. Try a smaller copy or a PDF.";
  return "";
}

/** The Anthropic content block for a document: PDFs as `document`, photos as `image`. */
export function contentBlock(type, base64) {
  const t = String(type || "").toLowerCase();
  return EXTRACT_TYPES[t] === "document"
    ? { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } }
    : { type: "image", source: { type: "base64", media_type: t, data: base64 } };
}
